import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";
import type {
  Department,
  DepartmentTab,
  FilterCriteria,
  QCInspection,
  QCConcreteTest,
  QCCylinderBreak,
} from "types/qc.types";

// Mock scheduled products data
const MOCK_SCHEDULED_PRODUCTS = [
  // Small Wet Department - Pipes
  {
    id: "prod-1",
    productName: '48" RCP Class III',
    jobNumber: "J-2024-0847",
    formNumber: "F-48-001",
    castDate: "2025-12-23",
    department: "small-wet",
    hasDrawing: true,
  },
  {
    id: "prod-2",
    productName: '36" RCP Class IV',
    jobNumber: "J-2024-0847",
    formNumber: "F-36-002",
    castDate: "2025-12-23",
    department: "small-wet",
    hasDrawing: true,
  },
  {
    id: "prod-3",
    productName: '24" RCP Class V',
    jobNumber: "J-2024-0850",
    formNumber: "F-24-003",
    castDate: "2025-12-23",
    department: "small-wet",
    hasDrawing: true,
  },
  {
    id: "prod-4",
    productName: '30" RCP Class III',
    jobNumber: "J-2024-0851",
    formNumber: "F-30-004",
    castDate: "2025-12-24",
    department: "small-wet",
    hasDrawing: true,
  },
  {
    id: "prod-5",
    productName: '18" RCP Class IV',
    jobNumber: "J-2024-0852",
    formNumber: "F-18-005",
    castDate: "2025-12-24",
    department: "small-wet",
    hasDrawing: false,
  },
  // Back Deck Department - Box Culverts, Noise Walls
  {
    id: "prod-6",
    productName: "8x6 Box Culvert",
    jobNumber: "J-2024-0860",
    formNumber: "BC-8x6-001",
    castDate: "2025-12-23",
    department: "back-deck",
    hasDrawing: true,
  },
  {
    id: "prod-7",
    productName: "10x8 Box Culvert",
    jobNumber: "J-2024-0860",
    formNumber: "BC-10x8-002",
    castDate: "2025-12-23",
    department: "back-deck",
    hasDrawing: true,
  },
  {
    id: "prod-8",
    productName: "Noise Wall Panel A-12",
    jobNumber: "J-2024-0865",
    formNumber: "NW-A12-001",
    castDate: "2025-12-24",
    department: "back-deck",
    hasDrawing: true,
  },
  {
    id: "prod-9",
    productName: "Noise Wall Panel A-13",
    jobNumber: "J-2024-0865",
    formNumber: "NW-A13-002",
    castDate: "2025-12-24",
    department: "back-deck",
    hasDrawing: true,
  },
  {
    id: "prod-10",
    productName: "Wing Wall Section 4",
    jobNumber: "J-2024-0868",
    formNumber: "WW-04-001",
    castDate: "2025-12-26",
    department: "back-deck",
    hasDrawing: true,
  },
  {
    id: "prod-11",
    productName: "6x4 Box Culvert",
    jobNumber: "J-2024-0870",
    formNumber: "BC-6x4-003",
    castDate: "2025-12-26",
    department: "back-deck",
    hasDrawing: false,
  },
  {
    id: "prod-12",
    productName: "Retaining Wall Block R-22",
    jobNumber: "J-2024-0872",
    formNumber: "RW-R22-001",
    castDate: "2025-12-27",
    department: "back-deck",
    hasDrawing: true,
  },
  // Dry Pass Department - Manholes, T-Footings
  {
    id: "prod-13",
    productName: '48" Manhole Base',
    jobNumber: "J-2024-0880",
    formNumber: "MH-48B-001",
    castDate: "2025-12-23",
    department: "dry-pass",
    hasDrawing: true,
  },
  {
    id: "prod-14",
    productName: '48" Manhole Riser',
    jobNumber: "J-2024-0880",
    formNumber: "MH-48R-002",
    castDate: "2025-12-23",
    department: "dry-pass",
    hasDrawing: true,
  },
  {
    id: "prod-15",
    productName: '60" Manhole Base',
    jobNumber: "J-2024-0882",
    formNumber: "MH-60B-001",
    castDate: "2025-12-24",
    department: "dry-pass",
    hasDrawing: true,
  },
  {
    id: "prod-16",
    productName: "T-Footing TF-24",
    jobNumber: "J-2024-0885",
    formNumber: "TF-24-001",
    castDate: "2025-12-24",
    department: "dry-pass",
    hasDrawing: true,
  },
  {
    id: "prod-17",
    productName: "T-Footing TF-30",
    jobNumber: "J-2024-0885",
    formNumber: "TF-30-002",
    castDate: "2025-12-25",
    department: "dry-pass",
    hasDrawing: false,
  },
  {
    id: "prod-18",
    productName: '72" Manhole Cone',
    jobNumber: "J-2024-0888",
    formNumber: "MH-72C-001",
    castDate: "2025-12-26",
    department: "dry-pass",
    hasDrawing: true,
  },
];

// Mock inspections data
const MOCK_INSPECTIONS: Partial<QCInspection>[] = [
  // Small Wet inspections
  {
    id: "insp-1",
    productId: "prod-1",
    productName: '48" RCP Class III',
    companyId: "company-1",
    department: "small-wet",
    stage: "pre_pour",
    status: "approved",
    overallStatus: "pass",
    inspectedAt: "2025-12-23T08:30:00Z",
    inspector: { id: "user-1", firstName: "Mike", lastName: "Johnson", email: "mike.johnson@example.com" },
  },
  {
    id: "insp-2",
    productId: "prod-2",
    productName: '36" RCP Class IV',
    companyId: "company-1",
    department: "small-wet",
    stage: "pre_pour",
    status: "pending_approval",
    overallStatus: "pass",
    inspectedAt: "2025-12-23T09:15:00Z",
    inspector: { id: "user-1", firstName: "Mike", lastName: "Johnson", email: "mike.johnson@example.com" },
  },
  {
    id: "insp-3",
    productId: "prod-3",
    productName: '24" RCP Class V',
    companyId: "company-1",
    department: "small-wet",
    stage: "pre_pour",
    status: "approved",
    overallStatus: "pass",
    inspectedAt: "2025-12-22T14:00:00Z",
    inspector: { id: "user-2", firstName: "Sarah", lastName: "Williams", email: "sarah.williams@example.com" },
  },
  {
    id: "insp-4",
    productId: "prod-3",
    productName: '24" RCP Class V',
    companyId: "company-1",
    department: "small-wet",
    stage: "post_pour",
    status: "approved",
    overallStatus: "pass",
    inspectedAt: "2025-12-23T11:00:00Z",
    inspector: { id: "user-2", firstName: "Sarah", lastName: "Williams", email: "sarah.williams@example.com" },
  },
  // Back Deck inspections
  {
    id: "insp-5",
    productId: "prod-6",
    productName: "8x6 Box Culvert",
    companyId: "company-1",
    department: "back-deck",
    stage: "pre_pour",
    status: "approved",
    overallStatus: "pass",
    inspectedAt: "2025-12-23T07:45:00Z",
    inspector: { id: "user-3", firstName: "Tom", lastName: "Davis", email: "tom.davis@example.com" },
  },
  {
    id: "insp-6",
    productId: "prod-7",
    productName: "10x8 Box Culvert",
    companyId: "company-1",
    department: "back-deck",
    stage: "pre_pour",
    status: "pending_approval",
    overallStatus: "pass",
    inspectedAt: "2025-12-23T10:30:00Z",
    inspector: { id: "user-3", firstName: "Tom", lastName: "Davis", email: "tom.davis@example.com" },
  },
  {
    id: "insp-7",
    productId: "prod-8",
    productName: "Noise Wall Panel A-12",
    companyId: "company-1",
    department: "back-deck",
    stage: "pre_pour",
    status: "rejected",
    overallStatus: "fail",
    inspectedAt: "2025-12-22T16:00:00Z",
    inspector: { id: "user-3", firstName: "Tom", lastName: "Davis", email: "tom.davis@example.com" },
  },
  // Dry Pass inspections
  {
    id: "insp-8",
    productId: "prod-13",
    productName: '48" Manhole Base',
    companyId: "company-1",
    department: "dry-pass",
    stage: "pre_pour",
    status: "approved",
    overallStatus: "pass",
    inspectedAt: "2025-12-23T08:00:00Z",
    inspector: { id: "user-4", firstName: "Lisa", lastName: "Chen", email: "lisa.chen@example.com" },
  },
  {
    id: "insp-9",
    productId: "prod-14",
    productName: '48" Manhole Riser',
    companyId: "company-1",
    department: "dry-pass",
    stage: "pre_pour",
    status: "pending_approval",
    overallStatus: "pass",
    inspectedAt: "2025-12-23T09:30:00Z",
    inspector: { id: "user-4", firstName: "Lisa", lastName: "Chen", email: "lisa.chen@example.com" },
  },
  {
    id: "insp-10",
    productId: "prod-15",
    productName: '60" Manhole Base',
    companyId: "company-1",
    department: "dry-pass",
    stage: "pre_pour",
    status: "pending_approval",
    overallStatus: "pass",
    inspectedAt: "2025-12-23T11:45:00Z",
    inspector: { id: "user-4", firstName: "Lisa", lastName: "Chen", email: "lisa.chen@example.com" },
  },
];

// Mock concrete tests
const MOCK_CONCRETE_TESTS: Partial<QCConcreteTest>[] = [
  {
    id: "ct-1",
    companyId: "company-1",
    batchId: "251223-01",
    mixDesign: "Mix #8 (7000 PSI)",
    testDate: "2025-12-23",
    mixPsi: 7000,
    slump: 4.5,
    airContent: 5.2,
    concreteTemp: 72,
    testedBy: "user-1",
    createdAt: "2025-12-23T08:00:00Z",
    updatedAt: "2025-12-23T08:00:00Z",
  },
  {
    id: "ct-2",
    companyId: "company-1",
    batchId: "251222-03",
    mixDesign: "SCC Mix (5000 PSI)",
    testDate: "2025-12-22",
    mixPsi: 5000,
    slump: 8.0,
    airContent: 6.1,
    concreteTemp: 68,
    testedBy: "user-2",
    createdAt: "2025-12-22T07:30:00Z",
    updatedAt: "2025-12-22T07:30:00Z",
  },
  {
    id: "ct-3",
    companyId: "company-1",
    batchId: "251221-02",
    mixDesign: "Mix #5 (4000 PSI)",
    testDate: "2025-12-21",
    mixPsi: 0, // N/A - waiting for results
    slump: 5.0,
    airContent: 5.5,
    concreteTemp: 70,
    testedBy: "user-3",
    createdAt: "2025-12-21T06:45:00Z",
    updatedAt: "2025-12-21T06:45:00Z",
  },
];

// Mock cylinder breaks - include one due today
const today = new Date().toISOString().split("T")[0];
const MOCK_CYLINDER_BREAKS: Partial<QCCylinderBreak>[] = [
  {
    id: "cb-1",
    concreteTestId: "ct-1",
    sampleId: "251223-01-A",
    age: 1,
    breakDate: today, // Due today!
    psiResult: 0,
    testedBy: "user-1",
    createdAt: "2025-12-23T08:00:00Z",
    updatedAt: "2025-12-23T08:00:00Z",
  },
  {
    id: "cb-2",
    concreteTestId: "ct-1",
    sampleId: "251223-01-B",
    age: 7,
    breakDate: "2025-12-29",
    psiResult: 0,
    testedBy: "user-1",
    createdAt: "2025-12-23T08:00:00Z",
    updatedAt: "2025-12-23T08:00:00Z",
  },
  {
    id: "cb-3",
    concreteTestId: "ct-2",
    sampleId: "251222-03-A",
    age: 7,
    breakDate: "2025-12-29",
    psiResult: 0,
    testedBy: "user-2",
    createdAt: "2025-12-22T07:30:00Z",
    updatedAt: "2025-12-22T07:30:00Z",
  },
  {
    id: "cb-4",
    concreteTestId: "ct-3",
    sampleId: "251221-02-A",
    age: 28,
    breakDate: "2026-01-18",
    psiResult: 0,
    testedBy: "user-3",
    createdAt: "2025-12-21T06:45:00Z",
    updatedAt: "2025-12-21T06:45:00Z",
  },
];

/**
 * QC Inspector Dashboard Store
 * Manages the inspector's view of scheduled products and inspections by department
 */
class QCInspectorDashboardStore {
  rootStore: RootStore;

  // UI State
  activeTab = 0;
  loading = false;
  error: string | null = null;

  // Mock data flag - set to true to use mock data
  useMockData = true;

  // Filters
  selectedDepartment: Department = "small-wet";
  filterCriteria: FilterCriteria = { department: "small-wet" };

  // Department tab configuration
  departmentTabs: DepartmentTab[] = [
    { label: "SMALL WET", value: "small-wet" },
    { label: "BACK DECK", value: "back-deck" },
    { label: "DRY PASS", value: "dry-pass" },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ============= Mock Data Getters =============

  get mockScheduledProducts() {
    return MOCK_SCHEDULED_PRODUCTS;
  }

  get mockInspections() {
    return MOCK_INSPECTIONS;
  }

  get mockConcreteTests() {
    return MOCK_CONCRETE_TESTS;
  }

  get mockCylinderBreaks() {
    return MOCK_CYLINDER_BREAKS;
  }

  // ============= Actions =============

  setActiveTab = (tab: number) => {
    this.activeTab = tab;
    this.selectedDepartment = this.departmentTabs[tab].value;
    this.filterCriteria.department = this.selectedDepartment;
    this.loadDepartmentData();
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  loadDashboardData = async () => {
    if (this.useMockData) {
      // Using mock data - no need to load
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const inspectionsStore = this.rootStore[
        "qc-inspectionsStore"
      ] as ServiceStore;

      if (inspectionsStore) {
        await inspectionsStore.find({
          query: {
            department: this.selectedDepartment,
            $sort: { createdAt: -1 },
          },
        });
      }

      runInAction(() => {
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load dashboard data";
        this.loading = false;
      });
    }
  };

  loadDepartmentData = async () => {
    await this.loadDashboardData();
  };

  // ============= Computed Values =============

  get inspectionsStore(): ServiceStore | null {
    return (this.rootStore["qc-inspectionsStore"] as ServiceStore) || null;
  }

  get allInspections(): QCInspection[] {
    if (this.useMockData) {
      return MOCK_INSPECTIONS as QCInspection[];
    }
    if (!this.inspectionsStore?.entities) return [];
    return this.inspectionsStore.entities as unknown as QCInspection[];
  }

  get inspections(): QCInspection[] {
    return this.allInspections.filter(
      (i) => i.department === this.selectedDepartment
    );
  }

  get scheduledProducts(): any[] {
    if (this.useMockData) {
      return MOCK_SCHEDULED_PRODUCTS.filter(
        (p) => p.department === this.selectedDepartment
      );
    }
    const productsStore = this.rootStore.productsStore as ServiceStore;
    if (!productsStore?.entities) return [];

    return productsStore.entities.filter(
      (p: any) => p.department === this.selectedDepartment
    );
  }

  get pendingInspections(): QCInspection[] {
    return this.allInspections.filter((i) => i.status === "pending_approval");
  }

  get todaysInspections(): QCInspection[] {
    const todayStr = new Date().toISOString().split("T")[0];
    return this.allInspections.filter((i) => i.inspectedAt?.startsWith(todayStr));
  }

  get completedToday(): number {
    return this.todaysInspections.filter((i) => i.status === "approved").length;
  }

  get failedInspections(): QCInspection[] {
    return this.allInspections.filter((i) => i.overallStatus === "fail");
  }

  // Concrete Tests
  get recentConcreteTests(): QCConcreteTest[] {
    if (this.useMockData) {
      return MOCK_CONCRETE_TESTS as QCConcreteTest[];
    }
    // Would load from concrete tests service store
    return [];
  }

  // Cylinder Breaks
  get upcomingCylinderBreaks(): QCCylinderBreak[] {
    if (this.useMockData) {
      return MOCK_CYLINDER_BREAKS as QCCylinderBreak[];
    }
    return [];
  }

  get cylinderBreaksDueToday(): QCCylinderBreak[] {
    const todayStr = new Date().toISOString().split("T")[0];
    return this.upcomingCylinderBreaks.filter(
      (b) => b.breakDate === todayStr && b.psiResult === 0
    );
  }

  // ============= Reset =============

  reset = () => {
    this.activeTab = 0;
    this.selectedDepartment = "small-wet";
    this.filterCriteria = { department: "small-wet" };
    this.loading = false;
    this.error = null;
  };
}

export default QCInspectorDashboardStore;
